import { computed, unref } from "vue";

/**
 * @typedef {Object} StepDetailsInput
 * @property {string[]} [todo]
 * @property {Array<{ label: string, value: string }>} [doneData]
 * @property {string|null} [warning]
 * @property {string|null} [actionLabel]
 * @property {string|null} [targetTab]
 */

/**
 * @typedef {Object} StepDefinition
 * @property {string} key
 * @property {number} [index]
 * @property {string} title
 * @property {string} description
 * @property {string} icon
 * @property {boolean} done
 * @property {string} [summary]
 * @property {StepDetailsInput} [details]
 */

/**
 * @typedef {('todo'|'current'|'done')} StepStatus
 */

/**
 * @typedef {Object} Step
 * @property {string} key
 * @property {number} index
 * @property {string} title
 * @property {string} description
 * @property {string} icon
 * @property {StepStatus} status
 * @property {boolean} done
 * @property {string} summary
 * @property {boolean} isCurrentTab
 * @property {StepDetailsInput} details
 */

/**
 * Generic stepper state composable.
 *
 * @param {Object} options
 * @param {import('vue').Ref<StepDefinition[]>|import('vue').ComputedRef<StepDefinition[]>|StepDefinition[]} options.definitions
 * @param {import('vue').Ref<string>|string} [options.activeTab]
 * @returns {{
 *   steps: import('vue').ComputedRef<Step[]>,
 *   doneCount: import('vue').ComputedRef<number>,
 *   totalSteps: import('vue').ComputedRef<number>,
 *   progress: import('vue').ComputedRef<number>,
 *   currentStepKey: import('vue').ComputedRef<string|null>,
 *   firstNotDoneStep: import('vue').ComputedRef<Step|null>
 * }}
 */
export function useStepperState({ definitions, activeTab } = {}) {
  const defsRef = () => {
    const v = unref(definitions);
    return Array.isArray(v) ? v : [];
  };

  const activeTabRef = () => {
    if (activeTab === undefined || activeTab === null) return null;
    return unref(activeTab);
  };

  const steps = computed(() => {
    const list = defsRef();
    const firstTodoIndex = list.findIndex((d) => !d.done);
    const at = activeTabRef();
    return list.map((def, i) => {
      const status =
        def.done === true ? "done" : firstTodoIndex === i ? "current" : "todo";
      const targetTab = def.details?.targetTab || null;
      const isCurrentTab = Boolean(targetTab) && targetTab === at;
      return {
        key: def.key,
        index: def.index ?? i + 1,
        title: def.title || "",
        description: def.description || "",
        icon: def.icon || "sli-main",
        done: def.done === true,
        status,
        summary: def.summary || "",
        isCurrentTab,
        details: {
          todo: def.details?.todo || [],
          doneData: def.details?.doneData || [],
          warning: def.details?.warning || null,
          actionLabel: def.details?.actionLabel || null,
          targetTab,
        },
      };
    });
  });

  const totalSteps = computed(() => steps.value.length);

  const doneCount = computed(
    () => steps.value.filter((s) => s.status === "done").length
  );

  const progress = computed(() => {
    const total = totalSteps.value;
    if (total === 0) return 0;
    return Math.round((doneCount.value / total) * 100);
  });

  const firstNotDoneStep = computed(() => {
    return steps.value.find((s) => s.status !== "done") || null;
  });

  const currentStepKey = computed(() => {
    const current = steps.value.find((s) => s.status === "current");
    return current ? current.key : null;
  });

  return {
    steps,
    totalSteps,
    doneCount,
    progress,
    currentStepKey,
    firstNotDoneStep,
  };
}
